import React, { Fragment, ReactNode } from 'react';
import size from 'lodash/size';

import {
  ClassName,
  FetchItemsErrorMessage,
  FetchItemsFetched,
  FetchItemsIsPlaceholderData,
  FetchItemsLimit,
  FetchItemsPage,
  FetchItemsPaginateItems,
  FetchItemsPrefetchItems,
  FetchItemsTotalCount,
  I18nText
} from '../../../../../types';

import { IndexTable } from '../IndexTable';

import { PaginationForm } from '../../../forms/PaginationForm';

import { AlertMessage } from '../../../../../helpers/AlertMessage';
import { NoResults } from '../../../../../helpers/NoResults';
import { NextPureLinkHelper } from '../../../../../helpers/links/NextPureLinkHelper';
import { LoadingTableSkeleton } from '../../../../../helpers/LoadingTableSkeleton';

import { words } from '../../../../../locales/keys';

type ItemsIndexTableWrapperItem = unknown;

interface ItemsIndexTableWrapperBaseProps {
  items: ItemsIndexTableWrapperItem[];
  itemsFetched: FetchItemsFetched;
  itemsError: FetchItemsErrorMessage;
  itemsIsPlaceholderData: FetchItemsIsPlaceholderData;
  itemsTotalCount: FetchItemsTotalCount;
  scope: string;
  children: ReactNode;
  noResultsI18nText?: I18nText;
  noResultsBody?: ReactNode;
  indexTableClassName?: ClassName;
  scrollContainerClassName?: ClassName;
  viewAllItemsPath?: string;
  settings?: ReactNode;
  skeletonRows?: number;
  withEmptyTable?: boolean;
  withSinglePagePaginationForm?: boolean;
}

interface ItemsIndexTableWrapperPaginationProps {
  itemsPage: FetchItemsPage;
  itemsLimit: FetchItemsLimit;
  paginateItems: FetchItemsPaginateItems;
  prefetchItems: FetchItemsPrefetchItems;
}

interface ItemsIndexTableWrapperWithoutPaginationFormProps
  extends Partial<ItemsIndexTableWrapperPaginationProps> {
  withoutPaginationForm: true;
}

interface ItemsIndexTableWrapperWithPaginationFormProps
  extends ItemsIndexTableWrapperPaginationProps {
  withoutPaginationForm?: false;
}

type ItemsIndexTableWrapperProps = ItemsIndexTableWrapperBaseProps &
  (
    | ItemsIndexTableWrapperWithoutPaginationFormProps
    | ItemsIndexTableWrapperWithPaginationFormProps
  );

function ItemsIndexTableWrapper({
  items,
  itemsFetched,
  itemsError,
  itemsIsPlaceholderData,
  itemsPage,
  itemsLimit,
  itemsTotalCount,
  paginateItems,
  prefetchItems,
  scope,
  children,
  noResultsI18nText,
  noResultsBody,
  indexTableClassName,
  scrollContainerClassName,
  viewAllItemsPath,
  settings,
  withoutPaginationForm = false,
  skeletonRows,
  withEmptyTable = false,
  withSinglePagePaginationForm = false
}: ItemsIndexTableWrapperProps) {
  if (!withEmptyTable && itemsFetched && itemsTotalCount === 0) {
    return noResultsBody ? (
      <div className="text-center my-5">{noResultsBody}</div>
    ) : (
      <NoResults
        addErrorClassName="m-4"
        errorMessage={itemsError}
        noResultsI18nText={noResultsI18nText}
      />
    );
  }

  return (
    <Fragment>
      <AlertMessage addClassName="m-4" message={itemsError} />
      <LoadingTableSkeleton
        loaded={itemsIsPlaceholderData || itemsFetched}
        rows={skeletonRows}
      >
        {size(items) > 0 || withEmptyTable ? (
          <Fragment>
            <IndexTable
              settings={settings}
              scope={scope}
              className={indexTableClassName}
              scrollContainerClassName={scrollContainerClassName}
            >
              {children}
            </IndexTable>

            {viewAllItemsPath ? (
              <div className="text-center my-6">
                <NextPureLinkHelper
                  href={viewAllItemsPath}
                  className="py-2 pl-4 pr-4 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-6 focus:ring-base border border-gray-300 dark:border-gray-700 shadow-sm bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                  i18nText={words.viewAll}
                />
              </div>
            ) : null}
          </Fragment>
        ) : null}
      </LoadingTableSkeleton>

      {withoutPaginationForm || !itemsFetched ? null : (
        <div className="px-4">
          <PaginationForm
            page={itemsPage}
            limit={itemsLimit}
            totalItemsCount={itemsTotalCount}
            onPaginate={paginateItems}
            onPaginateMouseEnter={prefetchItems}
            withSinglePagePaginationForm={withSinglePagePaginationForm}
          />
        </div>
      )}
    </Fragment>
  );
}

export default ItemsIndexTableWrapper;
